import { Box, FormControlLabel, InputLabel, Typography } from '@mui/material';
import { DIFFERENT_COLOR, TYPE_ANALYTICS_MAP_VIEW } from 'common/defines/constants';
import PieChartCommon from 'components/Common/PieChartCommon';
import SkeletonCharts from 'components/Common/SkeletonCharts';
import { SwitchCustom } from 'components/Common/SwitchCustom';
import { useClientSettingsStyle } from 'components/Dashboard/ClientSettings/ClientSettingsStyle';
import { QUERY_KEY } from 'constants/constants';
import { get, isEmpty } from 'lodash';
import randomColor from 'randomcolor';
import { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import {
  getSizeSoilErosionFieldAnalytic,
  getSoilErosionFieldAnalytic,
} from 'services/analytics/apiAnalyticsConfig.services';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { changeSizeSoilErosionFieldList, mapViewSelector, setDataSoilErosionField } from 'store/slices/mapViewSlice';
import SoilErosionFieldCardLayer from '../../Layer/SoilErosionField';
import { SwipeableViewsCustom } from '../../SwipeableViewsCustom';
import InfoTable from '../CropCover/InfoTable';

const SoilErosionFieldAnalytics = () => {
  const dispatch = useAppDispatch();
  const [chartMode, setChartMode] = useState(false);
  const classes = useClientSettingsStyle();

  const { analyticId, sizeSoilErosionFieldSelected, dataSoilErosionField, sizeSoilErosionFieldList } =
    useAppSelector(mapViewSelector);

  useQuery(
    [QUERY_KEY.SIZE_SOIL_EROSION_FIELD_ANALYTIC, analyticId],
    () => getSizeSoilErosionFieldAnalytic(analyticId || ''),
    {
      enabled: !!analyticId && isEmpty(sizeSoilErosionFieldList),
      onSuccess: (response) => {
        if (response.data) {
          dispatch(changeSizeSoilErosionFieldList(response.data));
        }
      },
    }
  );

  const { data: dataCropType } = useQuery(
    [QUERY_KEY.SOIL_EROSION_FIELD_ANALYTIC, analyticId, sizeSoilErosionFieldSelected],
    () => getSoilErosionFieldAnalytic(analyticId || '', sizeSoilErosionFieldSelected + ''),
    { enabled: !!analyticId && !!sizeSoilErosionFieldSelected }
  );

  const dataPie = useMemo(() => {
    if (dataSoilErosionField) {
      return dataSoilErosionField.map((_item: any) => ({
        name: _item.type,
        value: _item.lduseArea,
        totalTree: _item.totalTree,
      }));
    }
    return [];
  }, [dataSoilErosionField]);

  const getDataChart = useMemo(
    () =>
      dataSoilErosionField?.map((_item: any) => ({
        name: _item.type,
        value: chartMode ? _item.totalTree : _item.lduseArea,
      })),
    [chartMode, dataSoilErosionField]
  );

  const hasTreeCount = !!(dataSoilErosionField?.length && dataSoilErosionField.some((item: any) => item.totalTree > 0));

  const colorSoilErosionField = useMemo(() => {
    if (dataCropType?.data) {
      let colors: string[] = [];
      dispatch(
        setDataSoilErosionField(
          dataCropType.data.map((item: any, index: number) => {
            const colorRandom = randomColor({ luminosity: 'dark' });
            const _color = item.color || get(DIFFERENT_COLOR, `[${index}]`, colorRandom);
            colors.push(_color);
            return {
              ...item,
              visible: true,
              color: _color,
            };
          })
        )
      );
      return colors;
    }
    return [];
  }, [dataCropType, dispatch]);

  return (
    <SwipeableViewsCustom
      isDisableLabel
      analyticName={TYPE_ANALYTICS_MAP_VIEW.SOIL_EROSION_FIELD}
      analyticTab={
        <>
          {dataSoilErosionField?.length > 0 ? (
            <>
              <PieChartCommon title="Soil Erosion Field" dataChart={getDataChart} colorCharts={colorSoilErosionField} />
              {hasTreeCount && (
                <>
                  <InputLabel sx={{ marginLeft: '25px', fontSize: '14px' }}>Chart Mode:</InputLabel>
                  <FormControlLabel
                    sx={{ marginLeft: '30px' }}
                    control={
                      <SwitchCustom
                        checked={chartMode}
                        onClick={() => {
                          setChartMode(!chartMode);
                        }}
                      />
                    }
                    label={<Typography className={classes.textSetting}>{chartMode ? 'Tree Count' : 'Area'}</Typography>}
                  />
                </>
              )}
              <Box component={'div'} sx={{ marginTop: '30px' }}>
                <InfoTable dataPie={dataPie} hasTreeCount={hasTreeCount} />
              </Box>
            </>
          ) : (
            <>
              <SkeletonCharts count={1} />
            </>
          )}
        </>
      }
      layerTab={<SoilErosionFieldCardLayer labelCard={TYPE_ANALYTICS_MAP_VIEW.SOIL_EROSION_FIELD} />}
      isShowDownloadButton={dataSoilErosionField?.length > 0}
    />
  );
};

export default SoilErosionFieldAnalytics;
